import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Building, Sensor, Alert, PaginatedResponse } from '@/types';

// Define the API base query
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3001/api',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as any).auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const buildingsApi = createApi({
  reducerPath: 'buildingsApi',
  baseQuery,
  tagTypes: ['Building', 'Sensor', 'Alert'],
  endpoints: (builder) => ({
    // Get all buildings
    getBuildings: builder.query<PaginatedResponse<Building>, { page?: number; limit?: number }>({
      queryFn: async () => {
        const mockBuildings: Building[] = [
          {
            id: '1',
            name: 'Merkez Bina',
            address: 'İstanbul, Türkiye',
            coordinates: { lat: 41.0082, lng: 28.9784 },
            status: 'active',
            sensors: [],
            alerts: [],
            lastUpdated: new Date(),
          },
          {
            id: '2',
            name: 'Şube Bina',
            address: 'Ankara, Türkiye',
            coordinates: { lat: 39.9334, lng: 32.8597 },
            status: 'active',
            sensors: [],
            alerts: [],
            lastUpdated: new Date(),
          },
        ];

        return {
          data: {
            data: mockBuildings,
            total: mockBuildings.length,
            page: 1,
            limit: 10,
            totalPages: 1,
          },
        };
      },
      providesTags: ['Building'],
    }),

    // Get building by ID
    getBuildingById: builder.query<Building, string>({
      queryFn: async (id) => {
        const mockBuilding: Building = {
          id,
          name: `Bina ${id}`,
          address: 'İstanbul, Türkiye',
          coordinates: { lat: 41.0082, lng: 28.9784 },
          status: 'active',
          sensors: [
            {
              id: 'sensor-1',
              buildingId: id,
              name: 'Sıcaklık Sensörü',
              type: 'temperature',
              value: 22.5,
              unit: '°C',
              status: 'online',
              lastReading: new Date(),
              threshold: { min: 18, max: 26 },
            },
          ],
          alerts: [],
          lastUpdated: new Date(),
        };

        return { data: mockBuilding };
      },
      providesTags: (result, error, id) => [{ type: 'Building', id }],
    }),

    // Get sensors for a building
    getSensors: builder.query<Sensor[], string>({
      queryFn: async (buildingId) => {
        const mockSensors: Sensor[] = [
          {
            id: 'sensor-1',
            buildingId,
            name: 'Sıcaklık Sensörü',
            type: 'temperature',
            value: 22.5,
            unit: '°C',
            status: 'online',
            lastReading: new Date(),
            threshold: { min: 18, max: 26 },
          },
          {
            id: 'sensor-2',
            buildingId,
            name: 'Nem Sensörü',
            type: 'humidity',
            value: 65.2,
            unit: '%',
            status: 'online',
            lastReading: new Date(),
            threshold: { min: 40, max: 80 },
          },
          {
            id: 'sensor-3',
            buildingId,
            name: 'Basınç Sensörü',
            type: 'pressure',
            value: 1013.25,
            unit: 'hPa',
            status: 'offline',
            lastReading: new Date(Date.now() - 3600000), // 1 hour ago
            threshold: { min: 1000, max: 1020 },
          },
        ];

        return { data: mockSensors };
      },
      providesTags: (result, error, buildingId) => [
        { type: 'Sensor', id: buildingId },
      ],
    }),

    // Get alerts for a building
    getAlerts: builder.query<Alert[], string>({
      queryFn: async (buildingId) => {
        const mockAlerts: Alert[] = [
          {
            id: 'alert-1',
            buildingId,
            type: 'critical',
            priority: 'high',
            message: 'Sıcaklık eşiği aşıldı',
            timestamp: new Date(),
            isResolved: false,
            sensorId: 'sensor-1',
          },
          {
            id: 'alert-2',
            buildingId,
            type: 'warning',
            priority: 'medium',
            message: 'Nem seviyesi yüksek',
            timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
            isResolved: true,
            sensorId: 'sensor-2',
          },
        ];

        return { data: mockAlerts };
      },
      providesTags: (result, error, buildingId) => [
        { type: 'Alert', id: buildingId },
      ],
    }),

    // Create building
    createBuilding: builder.mutation<Building, Partial<Building>>({
      queryFn: async (building) => {
        const newBuilding: Building = {
          id: Date.now().toString(),
          name: building.name || 'Yeni Bina',
          address: building.address || 'Adres belirtilmemiş',
          coordinates: building.coordinates || { lat: 0, lng: 0 },
          status: 'active',
          sensors: [],
          alerts: [],
          lastUpdated: new Date(),
        };

        return { data: newBuilding };
      },
      invalidatesTags: ['Building'],
    }),

    // Update building
    updateBuilding: builder.mutation<Building, { id: string; updates: Partial<Building> }>({
      queryFn: async ({ id, updates }) => {
        const updatedBuilding: Building = {
          id,
          name: updates.name || `Bina ${id}`,
          address: updates.address || 'Adres belirtilmemiş',
          coordinates: updates.coordinates || { lat: 0, lng: 0 },
          status: updates.status || 'active',
          sensors: updates.sensors || [],
          alerts: updates.alerts || [],
          lastUpdated: new Date(),
        };

        return { data: updatedBuilding };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Building', id }],
    }),

    // Delete building
    deleteBuilding: builder.mutation<void, string>({
      queryFn: async (id) => {
        // Simulate successful deletion
        return { data: undefined };
      },
      invalidatesTags: ['Building'],
    }),
  }),
});

export const {
  useGetBuildingsQuery,
  useGetBuildingByIdQuery,
  useGetSensorsQuery,
  useGetAlertsQuery,
  useCreateBuildingMutation,
  useUpdateBuildingMutation,
  useDeleteBuildingMutation,
} = buildingsApi; 